import React, { SetStateAction, useEffect, useMemo, useState, useRef, useCallback } from "react";
import Modal, { ModalBody, ModalFooter } from "../../ui/modal";
import { AppBtn } from "../../ui/buttons";
import {
  capitalizeFirstLetter,
  enumToHumanFriendly,
  genarateStringFromVariantValues,
  objectIsFilled,
  toCurrency,
} from "../../../assets/js/utils/functions";
import SelectVariantExtra from "../items/select-variant-extra";
import {
  getActualPrice,
  checkItemAvailability,
  getItemThumbnail,
  getCustomVariantOption,
} from "../../../assets/js/utils/utils";
import { VariantItem, ProductItemInterface, CartItem } from "../../../assets/interfaces";
import RoundCheckbox from "../../ui/form-elements/round-checkbox";
import { FormatCurrencyFun } from "@/contexts/cart-context";
import SelectMultipleCustomOptions from "../items/select-multiple-custom-options";
import ErrorLabel from "@/components/ui/error-label";
import QuantityUpdate from "../items/quantity-update";
import SelectImageVariant from "../items/select-image-variant";
import SelectSingleCustomOptions from "../items/select-single-custom-options";
import CartContextV2 from "@/contexts/cart-context-v2";
import SelectMultipleImageVariants from "../items/select-multiple-image-variants";

type DynamicValueType = {
  [key: string]: string;
};

export enum StateType {
  FORM = "form",
  PREVIEW = "preview",
}

export enum ImageOptionsStep {
  START = "start",
  EXTRA = "extra",
}

export interface VariantsInternalStateItem {
  state: StateType;
  values: { [key: string]: string };
  image: string;
  id: string;
  quantity: number;
  error: string;
}

interface Props {
  show: boolean;
  toggle: (status: boolean) => void;
  product: ProductItemInterface;
}

const SelectVariantModal: React.FC<Props> = ({ show, toggle, product }) => {
  const { cart, addOrRemoveItem, formatAsCurrency } = CartContextV2.useContainer();

  const [imageOptionsStep, setImageOptionsStep] = useState<ImageOptionsStep>(ImageOptionsStep.START);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedOptionsError, setSelectedOptionsError] = useState<string | null>(null);
  const [internalSelected, setInternalSelected] = useState<VariantsInternalStateItem[]>([
    {
      state: StateType.FORM,
      values: Object.fromEntries(getOptionKeys(product).map((key) => [key, ""])) as { [key: string]: string },
      image: "",
      id: "",
      quantity: 1,
      error: "",
    },
  ]);

  const optionKeys = useMemo(() => {
    return getOptionKeys(product);
  }, [product]);

  const optionsWithComputedAvailability: VariantItem[] = useMemo(() => {
    return product?.variants.options.map((o) => {
      return { ...o, available_to_purchase: checkItemAvailability(product, o, true) };
    });
  }, [product]);

  const variantHasExtraOption = useMemo(
    () => product?.variants.type === "images" && product?.variants?.options[0]?.values !== undefined,
    [product]
  );

  //Update State Functions
  const updateOptionValues = (index: number, key: string, value: string) => {
    const updatedOptions = [...internalSelected];
    updatedOptions[index].values[key] = value;
    updatedOptions[index].error = "";

    setInternalSelected(updatedOptions);
  };

  const setOption = (index: number, payload: Partial<VariantsInternalStateItem>) => {
    const updatedOptions = [...internalSelected];
    updatedOptions[index] = { ...updatedOptions[index], ...payload };
    setInternalSelected(updatedOptions);
  };

  const updateOptionQuantity = (index: number, quantity: number) => {
    const updatedOptions = [...internalSelected];
    updatedOptions[index].quantity = quantity;
    updatedOptions[index].error = "";

    setInternalSelected(updatedOptions);
  };

  const saveSelections = (index: number) => {
    const updatedOptions = [...internalSelected];
    const selectedValues = updatedOptions[index].values;

    const isFilled = objectIsFilled(selectedValues);
    updatedOptions[index].error = isFilled ? "" : "Please select a value for all options";

    if (isFilled) {
      const variant = getCustomVariantOption(selectedValues, product?.variants.options);

      const previouslySelectedIndex = updatedOptions.findIndex((option) => option.id === variant?.id);

      if (previouslySelectedIndex !== -1 && previouslySelectedIndex !== index) {
        updatedOptions[index].error = "You've previously selected this option";
      } else {
        updatedOptions[index].id = variant.id;
        updatedOptions[index].state = StateType.PREVIEW;
        updatedOptions[index].error = "";
      }
    }

    setInternalSelected(updatedOptions);
    return updatedOptions;
  };

  const updateOptionState = (index: number, state: StateType) => {
    const updatedOptions = [...internalSelected];
    updatedOptions[index].state = state;
    setInternalSelected(updatedOptions);
  };

  const deleteOption = (index: number) => {
    const updatedOptions = [...internalSelected];
    updatedOptions.splice(index, 1);

    if (updatedOptions.length === 0) {
      setInternalSelected([getEmptyVariantItem(product)]);
    } else {
      setInternalSelected(updatedOptions);
    }
  };

  const addNewOption = () => {
    let updatedOptions = [...internalSelected];
    const lastOption = updatedOptions[updatedOptions.length - 1];

    if (!objectIsFilled(lastOption.values)) {
      updatedOptions[updatedOptions.length - 1].error = "Please select values to add new option";
      setInternalSelected(updatedOptions);
      return;
    }

    if (lastOption.id) {
      updatedOptions = saveSelections(updatedOptions.length - 1);
    }

    updatedOptions = updatedOptions.map((option) => ({ ...option, state: StateType.PREVIEW }));
    updatedOptions.push(getEmptyVariantItem(product));

    setInternalSelected(updatedOptions);
  };

  const saveSelectionsToCart = () => {
    const filteredInternalSelected = internalSelected.filter((i) => i.id);
    const variantIds = filteredInternalSelected.map((i) => i.id);
    const quantities = filteredInternalSelected.map((i) => i.quantity);

    const res = addOrRemoveItem(product, variantIds, quantities);

    if (typeof res?.errors === "string") {
      setSelectedOptionsError(res?.errors);
      return;
    } else {
      setSelectedOptionsError(null);
    }

    if (Object.keys(res?.errors ?? {}).length > 0) {
      const updatedSelected = internalSelected.map((i) => {
        if (res?.errors[i.id]) {
          return {
            ...i,
            error: res?.errors[i.id],
          };
        }

        return i;
      });

      setInternalSelected(updatedSelected);
      return;
    }

    toggle(false);
  };

  const getImageVariants = (): VariantItem[] => {
    if (variantHasExtraOption) {
      // const optionValues = Array.from(new Set(product?.variants.options.map((v) => Object.values(v.values)[0])));
      const optionImages: string[] = Array.from(new Set(product?.variants.options.map((o) => o.image)));

      const optionsAvailablity = product?.variants.options.map((o) => ({
        image: o.image,
        available: checkItemAvailability(product, o, true),
      }));

      return optionImages.map((o) => {
        const selectedVariant = internalSelected.find((item) => item.id === o);
        return {
          id: o,
          image: o,
          price: 0,
          is_available: product?.variants.options.some((v) => v.image === o && v.is_available),
          available_to_purchase: optionsAvailablity.some((v) => v.image === o && v.available),
          quantity: selectedVariant ? selectedVariant.quantity : 1,
        };
      });
    }

    return product?.variants.options.map((o) => ({
      ...o,
      available_to_purchase: checkItemAvailability(product, o, true),
      quantity: internalSelected.find((item) => item.id === o.id)
        ? internalSelected.find((item) => item.id === o.id)?.quantity
        : 1,
    }));
  };

  return (
    <Modal {...{ show, toggle }} title="Select Options" size="midi">
      <ModalBody>
        <div>
          {selectedOptionsError && (
            <div className="mb-3">
              <ErrorLabel error={selectedOptionsError} perm={true} />
            </div>
          )}

          {product?.variants.type === "images" && !variantHasExtraOption && (
            <SelectImageVariant
              {...{
                options: optionsWithComputedAvailability,
                formatAsCurrency,
                setOption,
                internalSelected,
                deleteOption,
                updateOptionQuantity,
              }}
            />
          )}

          {product?.variants.type === "images" && variantHasExtraOption && (
            <SelectMultipleImageVariants
              {...{
                step: imageOptionsStep,
                setStep: setImageOptionsStep,
                variantOptions: getImageVariants(),
                formatAsCurrency,
                product,
                optionKeys,
                internalSelected,
                deleteOption,
                updateOptionQuantity,
                selectedImages,
                setSelectedImages,
                updateOptionState,
                updateOptionValues,
                saveSelections,
                addNewOption,
                setInternalSelected,
              }}
            />
          )}

          {product?.variants.type === "custom" && optionKeys.length == 1 && (
            <>
              <h4 className="text-base text-black mb-2.5 font-bold">Select a {enumToHumanFriendly(optionKeys[0])}</h4>
              <SelectSingleCustomOptions
                {...{
                  variantOptions: optionsWithComputedAvailability,
                  formatAsCurrency,
                  setOption,
                  internalSelected,
                  deleteOption,
                  updateOptionQuantity,
                }}
              />
            </>
          )}

          {product?.variants.type === "custom" && optionKeys.length > 1 && (
            <SelectMultipleCustomOptions
              {...{
                formatAsCurrency,
                product,
                variantOptions: optionKeys,
                optionKeys,
                internalSelected,
                updateOptionState,
                updateOptionQuantity,
                deleteOption,
                updateOptionValues,
                saveSelections,
                addNewOption,
              }}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex items-center space-x-2.5 w-full">
          {!variantHasExtraOption || product?.variants.type !== "images" ? (
            <div className="flex-1 w-full">
              <AppBtn isBlock onClick={() => saveSelectionsToCart()} size="lg">
                Update Cart
              </AppBtn>
            </div>
          ) : (
            <div className="flex items-center space-x-2.5 w-full">
              {imageOptionsStep === ImageOptionsStep.EXTRA && (
                <div className="flex-1 w-full">
                  <AppBtn color="neutral" isBlock onClick={() => setImageOptionsStep(ImageOptionsStep.START)} size="lg">
                    Back
                  </AppBtn>
                </div>
              )}
              <div className="flex-1 w-full">
                <AppBtn
                  isBlock
                  onClick={() => {
                    if (imageOptionsStep === ImageOptionsStep.START) {
                      setImageOptionsStep(ImageOptionsStep.EXTRA);
                    } else {
                      saveSelectionsToCart();
                    }
                  }}
                  size="lg"
                >
                  {imageOptionsStep === ImageOptionsStep.START ? "Continue" : "Update Cart"}
                </AppBtn>
              </div>
            </div>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default SelectVariantModal;

const getOptionKeys = (product: ProductItemInterface) => {
  return !Object.keys(product?.variants?.options?.[0] ?? {}).length
    ? []
    : Object.keys(product?.variants?.options?.[0]?.values ?? {}).reverse();
};

const getEmptyVariantItem = (product: ProductItemInterface) => {
  return {
    state: StateType.FORM,
    values: Object.fromEntries(getOptionKeys(product).map((key) => [key, ""])) as { [key: string]: string },
    id: "",
    quantity: 1,
    error: "",
    image: "",
  };
};
